import React, { useCallback, useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import { message } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  MagnifyingGlassIcon,
  GlobeAltIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import loginLogApi from '../../services/loginLogApi';
import {
  PageHeader,
  TWInput,
  TWSelect,
  TWDataTable,
  StatusBadge,
  EmptyState,
} from '../../components/ui';
import { headerCls, DataTableSkeleton } from '../../data/columns';

dayjs.extend(utc);

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

const SHELL_CLASS =
  'login-logs-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8';

// "PasswordResetRequest" -> "Password Reset Request"
const humanize = (s) =>
  s ? s.replace(/([a-z0-9])([A-Z])/g, '$1 $2') : '';

const EVENT_TONE = {
  Login: 'blue',
  Register: 'sky',
  Impersonation: 'violet',
  TerminalPair: 'purple',
  OtpSend: 'sky',
  PasswordResetRequest: 'amber',
  PasswordReset: 'amber',
  Logout: 'slate',
};

const EVENT_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'Login', label: 'Login' },
  { value: 'Register', label: 'Register' },
  { value: 'Impersonation', label: 'Impersonation' },
  { value: 'TerminalPair', label: 'Terminal Pair' },
  { value: 'OtpSend', label: 'OTP Send' },
  { value: 'PasswordResetRequest', label: 'Password Reset Request' },
  { value: 'PasswordReset', label: 'Password Reset' },
  { value: 'Logout', label: 'Logout' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'Success' },
  { value: 'false', label: 'Failed' },
];

const USERTYPE_OPTIONS = [
  { value: '', label: 'All user types' },
  { value: 'AppUser', label: 'App User' },
  { value: 'Customer', label: 'Customer' },
  { value: 'Terminal', label: 'Terminal' },
  { value: 'KitchenTerminal', label: 'Kitchen Terminal' },
  { value: 'Unknown', label: 'Unknown' },
];

const fmtWhen = (v) =>
  v ? dayjs.utc(v).format('MMM D, YYYY · HH:mm') : '—';

const columns = [
  {
    title: <span className={headerCls}>When (UTC)</span>,
    key: 'when',
    render: (_, r) => (
      <span className="whitespace-nowrap text-sm tabular-nums text-slate-700 dark:text-slate-300">
        {fmtWhen(r.createdate)}
      </span>
    ),
  },
  {
    title: <span className={headerCls}>Event</span>,
    key: 'event',
    render: (_, r) => (
      <div>
        <StatusBadge
          tone={EVENT_TONE[r.eventtype] || 'slate'}
          label={humanize(r.eventtype) || '—'}
        />
        {r.loginmethod ? (
          <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
            {humanize(r.loginmethod)}
          </p>
        ) : null}
      </div>
    ),
  },
  {
    title: <span className={headerCls}>Status</span>,
    key: 'status',
    render: (_, r) => (
      <div>
        {r.success ? (
          <StatusBadge tone="emerald" label="Success" dot />
        ) : (
          <StatusBadge tone="rose" label="Failed" dot />
        )}
        {!r.success && r.failurereason ? (
          <p className="mt-1 text-xs text-rose-500 dark:text-rose-400">
            {humanize(r.failurereason)}
          </p>
        ) : null}
      </div>
    ),
  },
  {
    title: <span className={headerCls}>User</span>,
    key: 'user',
    render: (_, r) => {
      const primary = r.email || r.phone || r.userid || 'Unknown';
      return (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
            {primary}
          </p>
          {r.email && r.phone ? (
            <p className="truncate text-xs text-slate-400 dark:text-slate-500">
              {r.phone}
            </p>
          ) : null}
          {r.role ? (
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {r.role}
            </p>
          ) : null}
          {r.initiatoremail ? (
            <p className="mt-0.5 inline-flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-300">
              <UserCircleIcon className="h-3.5 w-3.5" />
              by {r.initiatoremail}
            </p>
          ) : null}
        </div>
      );
    },
  },
  {
    title: <span className={headerCls}>Type</span>,
    key: 'type',
    render: (_, r) => (
      <span className="text-sm text-slate-600 dark:text-slate-300">
        {humanize(r.usertype) || '—'}
      </span>
    ),
  },
  {
    title: <span className={headerCls}>IP / Location</span>,
    key: 'ip',
    render: (_, r) => {
      const body = (
        <div className="min-w-0">
          <span className="flex items-center gap-1 text-sm tabular-nums text-slate-600 dark:text-slate-300">
            <GlobeAltIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            {r.ipaddress || '—'}
          </span>
          {r.country ? (
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {r.country}
            </p>
          ) : null}
        </div>
      );
      return r.useragent ? (
        <Tooltip title={r.useragent}>{body}</Tooltip>
      ) : (
        body
      );
    },
  },
];

const LoginLogs = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [eventType, setEventType] = useState('');
  const [status, setStatus] = useState('');
  const [userType, setUserType] = useState('');
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    totalRecords: 0,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRows = useCallback(
    async (page, filters) => {
      setLoading(true);
      try {
        const parts = [
          `?page=${page}`,
          `&pagesize=${PAGE_SIZE}`,
          filters.email ? `&email=${encodeURIComponent(filters.email)}` : '',
          filters.eventtype ? `&eventtype=${filters.eventtype}` : '',
          filters.success !== '' ? `&success=${filters.success}` : '',
          filters.usertype ? `&usertype=${filters.usertype}` : '',
        ];
        const res = await loginLogApi.list(parts.join(''));
        setRows(res.data?.data || []);
        setPagination({
          pageNumber: page,
          pageSize: PAGE_SIZE,
          totalRecords: res.data?.totalRecords || 0,
        });
      } catch {
        setRows([]);
        message.error('Failed to load login logs');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    fetchRows(1, {
      email: debouncedSearch,
      eventtype: eventType,
      success: status,
      usertype: userType,
    });
  }, [debouncedSearch, eventType, status, userType, fetchRows]);

  const isEmpty = !loading && rows.length === 0;
  const hasFilters =
    debouncedSearch || eventType || status !== '' || userType;

  const headerActions =
    pagination.totalRecords > 0 && !loading ? (
      <span className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
        {Number(pagination.totalRecords).toLocaleString('en-US')} total
      </span>
    ) : null;

  return (
    <div className={SHELL_CLASS}>
      <div className="mx-auto max-w-6xl space-y-6">
        <PageHeader
          title="Login Logs"
          description="Authentication events across the platform — logins, registrations, impersonations and more."
          actions={headerActions}
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full max-w-xs">
            <TWInput
              leftIcon={MagnifyingGlassIcon}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email…"
              aria-label="Search login logs by email"
            />
          </div>
          <div className="w-full sm:w-52">
            <TWSelect
              value={eventType}
              onChange={setEventType}
              allowClear={false}
              options={EVENT_OPTIONS}
              aria-label="Filter by event"
            />
          </div>
          <div className="w-full sm:w-40">
            <TWSelect
              value={status}
              onChange={setStatus}
              allowClear={false}
              options={STATUS_OPTIONS}
              aria-label="Filter by status"
            />
          </div>
          <div className="w-full sm:w-44">
            <TWSelect
              value={userType}
              onChange={setUserType}
              allowClear={false}
              options={USERTYPE_OPTIONS}
              aria-label="Filter by user type"
            />
          </div>
        </div>

        {loading ? (
          <DataTableSkeleton
            rows={10}
            headers={[
              { label: 'When', w: 'w-32' },
              { label: 'Event', shape: 'pill', w: 'w-24' },
              { label: 'Status', shape: 'pill', w: 'w-20' },
              { label: 'User', w: 'w-40' },
              { label: 'Type', w: 'w-24' },
              { label: 'IP / Location', w: 'w-32' },
            ]}
          />
        ) : isEmpty ? (
          <EmptyState
            variant="dashed"
            title="No login logs found"
            description={
              hasFilters
                ? 'No events match your filters.'
                : 'Authentication events will appear here.'
            }
          />
        ) : (
          <TWDataTable
            columns={columns}
            data={rows}
            rowKey="id"
            pagination={
              pagination.totalRecords > PAGE_SIZE
                ? {
                    current: pagination.pageNumber,
                    pageSize: PAGE_SIZE,
                    total: pagination.totalRecords,
                    showSizeChanger: false,
                    showTotal: (total, range) =>
                      `Showing ${range[0]} to ${range[1]} of ${total} events`,
                    onChange: (page) =>
                      fetchRows(page, {
                        email: debouncedSearch,
                        eventtype: eventType,
                        success: status,
                        usertype: userType,
                      }),
                  }
                : undefined
            }
          />
        )}
      </div>
    </div>
  );
};

export default LoginLogs;
